import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect, useState } from "react";
import { Modal, Button, Select, TextInput, NumberInput, Flex, Image, Checkbox } from '@mantine/core';
import { useNavigate, useLocation } from "react-router-dom";
import logo from '../../assets/logo.png';
import { creatorOptions, defaultJson, apiEndpoints, messages, formTypes, recurrenceFrequencies, frequencyDays } from '../messages.js/BuilderTexts';
import { SurveyCreatorComponent, SurveyCreator, Serializer } from '@navriti/forms';

Serializer.findProperty("file", "maxSize").visible = false;
Serializer.findProperty("file", "allowMultiple").visible = true;
Serializer.addProperty("survey", {
  name: "oneResponsePerEmail:boolean",
  category: "general",
  displayName: "One response per email",
  default: false,
});

// Override the property editor behavior
Serializer.addProperty("file", {
  name: "maxSizeInMB:number",
  category: "general",
  visibleIndex: 15,
  displayName: "Maximum file size (MB)",
  description: "Enter file size in megabytes",
  default: 0,
  onSetValue: (obj, value) => {
    obj.maxSize = value * 1024 * 1024; // MB → bytes
  },
  onGetValue: (obj) => {
    return obj.maxSize ? obj.maxSize / (1024 * 1024) : 0;
  }
});
Serializer.findProperty("file", "acceptedTypes").visible = false;

const FILE_TYPE_MAP = {
  images: [".jpg", ".jpeg", ".png"],
  pdf: [".pdf"],
  word: [".doc", ".docx"],
  excel: [".xls", ".xlsx"],
  video: [".mp4"],
  zip: [".zip"]
};

Serializer.addProperty("file", {
  name: "acceptedTypesList",
  visibleIndex: 15,
  // type: "checkbox",
  isArray: true,
  category: "general",
  title: "Accepted file types",
    type: "multiplevalues",
  visible: true,
  choices: [
    { value: "images", text: "Images (.jpg, .png)" },
    { value: "pdf", text: "PDF (.pdf)" },
    { value: "word", text: "Word (.doc, .docx)" },
    { value: "excel", text: "Excel (.xls, .xlsx)" },
    { value: "video", text: "Videos (.mp4)" },
    { value: "zip", text: "Zip (.zip)" }
  ],
  default: [],

  onSetValue: (obj, value) => {
  // Normalize value to array
    const list = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [];

    if (list.length === 0) {
      obj.acceptedTypes = "";
      return;
    }

    const extensions = list.flatMap(v => FILE_TYPE_MAP[v] || []);
    obj.acceptedTypes = extensions.join(",");
  },


  onGetValue: (obj) => {
    if (!obj.acceptedTypes  || typeof obj.acceptedTypes !== "string") return [];

    const current = obj.acceptedTypes.split(",");

    return Object.entries(FILE_TYPE_MAP)
      .filter(([, exts]) => exts.some(e => current.includes(e)))
      .map(([key]) => key);
  }
});

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

// Function to get survey by title from API
async function getSurveyByTitle(templateName, token) {
  try {
    const response = await fetch(apiEndpoints.getTemplates, {
      method: "POST",
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: getCookie('email') })
    });

    if (response.ok) {
      const fetchedData = await response.json();
      if (fetchedData && fetchedData.templateData && Array.isArray(fetchedData.templateData)) {
        const matchingSurvey = fetchedData.templateData.find(item => 
          item.TemplateName === templateName
        );
        if (matchingSurvey) {
          return typeof matchingSurvey.TemplateData === 'string' 
            ? JSON.parse(matchingSurvey.TemplateData) 
            : matchingSurvey.TemplateData;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching template:', error);
    return null;
  }
}

export default function SurveyCreatorWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [link, setLink] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [type, setType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [duration, setDuration] = useState(1);
  const [frequency, setFrequency] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [creator, setCreator] = useState(null);
  const [notifyOwnerOnResponse, setNotifyOwnerOnResponse] = useState(true);
  const [initialJson, setInitialJson] = useState(
    window.localStorage.getItem("survey-json") || JSON.stringify(defaultJson)
  );
  useEffect(() => {
    const initializeSurveyCreator = async () => {
      const params = new URLSearchParams(location.search);
      const templateName = params.get('templateName');
      const useTemplate = params.get('useTemplate');
      
      let surveyJson = initialJson;

      if (useTemplate === 'true' && templateName) {
        const token = getCookie('token');
        if (token) {
          const survey = await getSurveyByTitle(templateName, token);
          if (survey) {
            survey.title = `Copy of ${survey.title || templateName}`;
            surveyJson = JSON.stringify(survey);
          } else {
            toast.error(messages.errorGeneral);
          }
        } else {
          toast.error(messages.errorNoToken);
        }
      }

      const surveyCreator = new SurveyCreator(creatorOptions);
      const hiddenTypes = [
          "multipletext",
          "paneldynamic",
          "matrix",
          "matrixdropdown",
          "matrixdynamic",
          "matrixrating",
          "imagepicker"
        ];

        hiddenTypes.forEach(type => {
          surveyCreator.toolbox.removeItem(type);
        });

      surveyCreator.text = surveyJson;

      (function attachDescriptionLimit(creatorInstance) {
        if (!creatorInstance) {
          console.warn('attachDescriptionLimit: creatorInstance is falsy');
          return;
        }

        // handler used for onValidateProperty-like APIs
        const validateHandler = (sender, options) => {
          try {
            if (
              options &&
              options.obj &&
              typeof options.obj.getType === 'function' &&
              options.obj.getType() === 'survey' &&
              options.propertyName === 'description'
            ) {
              const val = options.value || '';
              if (val.length > 1000) {
                options.value = val.substring(0, 1000);
                options.error = 'Max 1000 characters allowed.';
                toast.warn('Description cannot exceed 1000 characters (max 1000).');
              }
            }
          } catch (err) {
            console.error('validateHandler error', err);
          }
        };

        // handler used for onPropertyValueChanging-like APIs
        const changingHandler = (sender, options) => {
          try {
            if (
              options &&
              options.obj &&
              typeof options.obj.getType === 'function' &&
              options.obj.getType() === 'survey' &&
              options.propertyName === 'description'
            ) {
              const newValue = options.newValue || '';
              if (newValue.length > 1000) {
                options.newValue = newValue.substring(0, 1000);
                toast.warn('Description cannot exceed 1000 characters (max 1000).');
              }
            }
          } catch (err) {
            console.error('changingHandler error', err);
          }
        };

        // Try multiple event names (v1 and v2 variants). Attach where available.
        try {
          if (creatorInstance.onValidateProperty && typeof creatorInstance.onValidateProperty.add === 'function') {
            creatorInstance.onValidateProperty.add(validateHandler);
            console.info('attachDescriptionLimit: attached to onValidateProperty');
            return;
          }

          if (creatorInstance.onPropertyValueChanging && typeof creatorInstance.onPropertyValueChanging.add === 'function') {
            creatorInstance.onPropertyValueChanging.add(changingHandler);
            console.info('attachDescriptionLimit: attached to onPropertyValueChanging');
            return;
          }

          if (creatorInstance.onPropertyValueChanged && typeof creatorInstance.onPropertyValueChanged.add === 'function') {
            creatorInstance.onPropertyValueChanged.add(changingHandler);
            console.info('attachDescriptionLimit: attached to onPropertyValueChanged');
            return;
          }

          if (creatorInstance.onPropertyValidation && typeof creatorInstance.onPropertyValidation.add === 'function') {
            creatorInstance.onPropertyValidation.add((s, opts) => {
              // older variant sometimes sends opts differently
              validateHandler(s, opts);
            });
            console.info('attachDescriptionLimit: attached to onPropertyValidation');
            return;
          }

          // Last-resort: try onValidateProperty on creator.creator (some wrappers nest the real instance)
          if (creatorInstance.creator && creatorInstance.creator.onValidateProperty && typeof creatorInstance.creator.onValidateProperty.add === 'function') {
            creatorInstance.creator.onValidateProperty.add(validateHandler);
            console.info('attachDescriptionLimit: attached to creator.creator.onValidateProperty');
            return;
          }

          // If nothing matched — log keys to help debugging
          console.warn('attachDescriptionLimit: no matching event found on surveyCreator. Available keys:', Object.keys(creatorInstance));
          toast.error('Unable to attach description length limit — survey creator events unavailable. Check console.');
        } catch (err) {
          console.error('attachDescriptionLimit: unexpected error:', err);
          toast.error('Error attaching description validation. See console.');
        }
      })(surveyCreator);

      surveyCreator.onPropertyChanged?.add((sender, options) => {
        if (options.name === 'title') {
          console.log('Title changed to:', options.newValue);
          surveyCreator.saveSurvey();
          window.localStorage.setItem("survey-json", surveyCreator.text);
          setHasUnsavedChanges(true);
        }
      });

      surveyCreator.onModified?.add(() => {
        const isChanged = JSON.stringify(JSON.parse(surveyCreator.text)) !== JSON.stringify(JSON.parse(surveyJson));
        setHasUnsavedChanges(isChanged);
      });

      setCreator(surveyCreator);
      setInitialJson(surveyJson);
    };

    initializeSurveyCreator();

    return () => {
      window.localStorage.setItem("survey-json", creator?.text || initialJson);
    };
  }, [location.search]);

  const updateLink = async (id) => {
    const currentUrl = window.location.href;
    const baseUrl = new URL(currentUrl).origin;

    try {
      const token = getCookie('token');
      const encryptResponse = await fetch(apiEndpoints.encryptId, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ id })
      });

      if (!encryptResponse.ok) {
        throw new Error(messages.errorEncryptId);
      }

      const { encryptedId } = await encryptResponse.json();

      const updateResponse = await fetch(apiEndpoints.updateLink, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          formId: id,
          formLink: `${baseUrl}/forms?id=${encryptedId}`
        })
      });

      if (updateResponse.ok) {
       
        toast.success(messages.successFormSave, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
        setLink(`${baseUrl}/forms?id=${encryptedId}`);
        window.localStorage.removeItem("survey-json");
        setHasUnsavedChanges(false);
        
      } else {
        console.log("Link update failed"); 
        toast.error(messages.errorSaveLink);
      }
    } catch (error) {
      console.error("Error saving form LINK:", error);
      toast.error(messages.errorGeneral);
    }
  };

  const publishForm = async (isDraft) => {
    if (!creator) {
      toast.error("Survey Creator is not initialized.");
      return false;
    }

    try {
      await creator.saveSurvey();
      const creatorJSON = JSON.parse(creator.text);

      const surveyTitle = creator.survey.title || creatorJSON.title;
      if (!surveyTitle) {
        toast.error("Form name is missing. Please add a form name before saving.");
        return false;
      }

      if (!isDraft && type === "recurring" && (!duration || !frequency)) {
        toast.error(duration ? messages.errorFrequency : messages.errorDuration);
        return false;
      }
      if (!isDraft && type === "recurring" && duration > frequencyDays[frequency]) {
        toast.error(`Duration (${duration} days) cannot exceed ${frequencyDays[frequency]} days for ${frequency} frequency.`);
        return false;
      }
      if (!isDraft && type === "one-time" && (!startDate || !endDate)) {
        toast.error(messages.errordateRange);
        return false;
      }

      if (isDraft) {
        setIsSavingDraft(true);
      } else {
        setIsPublishing(true);
      }

      const token = getCookie('token');
      const requestBody = {
        formData: creatorJSON,
        formName: surveyTitle,
        type: type,
        isDraft: isDraft,
        email: getCookie('email'),
        notifyOwnerOnResponse: notifyOwnerOnResponse
      };

      if (!isDraft) {
        if (type === 'recurring') {
          requestBody.recurrence = { duration, frequency };
        } else if (type === 'one-time') {
          requestBody.dateRange = [new Date(startDate).toISOString(), new Date(endDate).toISOString()];
        } else if (type === 'open') {
          requestBody.dateRange = [];
        }
      }

      const response = await fetch(apiEndpoints.formSave, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        let res = await response.json();
       
        if (isDraft) {
          
          toast.success(messages.successFormSave, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
          window.localStorage.setItem("survey-json", creator.text);
          setHasUnsavedChanges(false);
          
          
        } else {
          console.log("Calling updateLink for publish");
          await updateLink(res.savedFormData.id);
        }
        return res.savedFormData.id;
      } else {
        const errorData = await response.json();
        console.error('formSave error response:', errorData);
        toast.error(errorData.error || messages.errorFormSave);
        return false;
      }
    } catch (error) {
      console.error("Error saving form data:", error);
      toast.error(messages.errorGeneral);
      return false;
    } finally {
      if (isDraft) {
        setIsSavingDraft(false);
      } else {
        setIsPublishing(false);
      }
    }
  };

  const handleModalClose = () => {
    setModalIsOpen(false);
    setType("");
    setStartDate("");
    setEndDate("");
    setDuration(1);
    setFrequency("");
  };

  const handleSubmitModal = async () => {
    if (!type) {
      toast.error("Please select a form type.");
      return;
    }

    if (type === "recurring" && (!duration || !frequency)) {
      toast.error(duration ? messages.errorFrequency : messages.errorDuration);
      return;
    }
    if (type === "recurring" && duration > frequencyDays[frequency]) {
      toast.error(`Duration (${duration} days) cannot exceed ${frequencyDays[frequency]} days for ${frequency} frequency.`);
      return;
    }

    if (type === "one-time" && (!startDate || !endDate)) {
      toast.error(messages.errordateRange);
      return;
    }

    if (type === "open") {
      const today = new Date();
      const nextYear = new Date(today);
      nextYear.setFullYear(today.getFullYear() + 1);
      setStartDate(today.toISOString().split('T')[0]);
      setEndDate(nextYear.toISOString().split('T')[0]);
    }

    setModalIsOpen(false);
    const result = await publishForm(false);
    if (result) {
      
      setType("");
      setStartDate("");
      setEndDate("");
      setDuration(1);
      setFrequency("");
    }
  };

  const handlePublishButtonClick = async () => {
    if (isPublishing) return;
    if (!creator) {
      toast.error("Survey Creator is not initialized.");
      return;
    }

    await creator.saveSurvey();
    window.localStorage.setItem("survey-json", creator.text);
    const creatorJSON = JSON.parse(creator.text);

    const surveyTitle = creator.survey.title || creatorJSON.title;
    if (!surveyTitle) {
      toast.error("Form name is missing. Please add a form name before saving.");
      return;
    }

    setModalIsOpen(true);
  };

  const handleSaveDraftButtonClick = async () => {
    if (isSavingDraft) return;
    await publishForm(true);
  };

  const handleBackToHome = () => {
    if (hasUnsavedChanges) {
      setConfirmModalOpen(true);
    } else {
      navigate('/');
    }
  };

  const handleConfirmDontSave = () => {
    setConfirmModalOpen(false);
    window.localStorage.removeItem("survey-json");
    navigate('/');
  };

  const handleConfirmCancel = () => {
    setConfirmModalOpen(false);
  };
  return (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {creator ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* <SurveyCreatorComponent creator={creator} /> */}
          <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 24px',
                backgroundColor: '#ffffffff',
                borderBottom: '1px solid #ddd',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                height: '60px',
              }}
      >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Flex align="center">
                      <a href="/"><Image src={logo} height={50} width={50} /></a>
                  </Flex>
                  <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                    Form Builder
                  </h2>
                </div>
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0, height: '60%' }}>
          <Button
            onClick={handleBackToHome}
            variant={confirmModalOpen ? "filled" : "outline"}
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          > 
            Back to Home
          </Button>
          <Button
            onClick={handleSaveDraftButtonClick}
            disabled={isSavingDraft || isPublishing}
            loading={isSavingDraft}
            variant="outline"
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          >
            Save as draft
          </Button>
          <Button
            onClick={handlePublishButtonClick}
            disabled={isSavingDraft || isPublishing}
            loading={isPublishing}
            variant={modalIsOpen ? "filled" : "outline"} 
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          >
            Publish Form
          </Button>
          {link &&
          <Button
              onClick={() => setLinkModalOpen(true)}
              variant={linkModalOpen ? "filled" : "outline"}            
              style={{
                borderRadius: '9px',
                margin: '0',
                height: '100%',
              }}
            >
              View Form Link
            </Button>}
        </div>
      </div>
             <div style={{ flex: 1, overflow: 'auto' }}>
                <SurveyCreatorComponent creator={creator} />
          </div>


          <Modal opened={modalIsOpen} onClose={handleModalClose} title="Choose Type"
              overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}>
            <Select
              label="Form Type"
              placeholder="Pick one"
              data={formTypes}
              value={type}
              onChange={setType}
            />
            {type === "one-time" && (
              <div>
                <TextInput
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.currentTarget.value)}
                  style={{ marginBottom: '10px' }}
                />
                <TextInput
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.currentTarget.value)}
                  style={{ marginBottom: '10px' }}
                />
              </div>
            )}
            {type === "recurring" && (
              <div>
                <Select
                  label="Recurrence Frequency"
                  placeholder="Select frequency"
                  data={recurrenceFrequencies}
                  value={frequency}
                  onChange={setFrequency}
                  style={{ marginBottom: '10px' }}
                />
                <NumberInput
                  label="Duration (days)"
                  value={duration}
                  onChange={(value) => setDuration(value)}
                  min={1}
                  max={frequency ? frequencyDays[frequency] : 30}
                  style={{ marginBottom: '10px' }}
                />
              </div>
            )}
            <Checkbox
              label="Notify me by email on each response"
              checked={notifyOwnerOnResponse}
              onChange={(e) => setNotifyOwnerOnResponse(e.currentTarget.checked)}
            style={{ marginBottom: '10px', marginTop: '10px' }}
            />
            <Button 
              onClick={handleSubmitModal}
              disabled={isPublishing}
              loading={isPublishing}
              style={{ marginRight: '10px' }}
            >
              Submit
            </Button>
            <Button onClick={handleModalClose} variant="light">
              Cancel
            </Button>
          </Modal>
          <Modal
            opened={confirmModalOpen}
            onClose={handleConfirmCancel}
            title="Unsaved Changes"
            centered
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
          >
            <p>Do you want to discard your changes?</p>
            <Button
              onClick={handleConfirmDontSave}
              variant="outline"
              style={{ marginRight: '10px' }}
            >
              Don't Save
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="light"
            >
              Cancel
            </Button>
          </Modal>
          <Modal
                opened={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                title="Form Link"
                centered
                overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
              >
                <p style={{ wordBreak: 'break-all', textAlign: 'center' }}>
                  <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#17a387ff' }}>
                    {link}
                  </a>
                </p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                    setLinkModalOpen(false);
                    toast.success('Form link copied to clipboard!');
                  }}
                  fullWidth
                  style={{ marginTop: '15px' }}
                >
                  Copy Link
                </Button>
                <Button
                  variant="light"
                  fullWidth
                  style={{ marginTop: '10px' }}
                  onClick={() => setLinkModalOpen(false)}
                >
                  Close
                </Button>
              </Modal>
          <ToastContainer />
        </div>
      ) : (
        <p style={{color: 'gray', fontSize: '25px', textAlign: 'center', marginTop: '20px'}}>Loading...</p>
      )}
    </div>
  );
}